import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { AdminLoginSchema, type AdminLoginInput } from "@steelbill/types"
import { cn } from "@/lib/utils"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"

/**
 * Admin sign-in — the same `login-04` block as the owner's screen, with email
 * and password instead of phone and PIN (§2). Validated with
 * `AdminLoginSchema`, the exact object `POST /auth/admin-login` uses.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * UNLISTED ON PURPOSE. Nothing in the app links here; you reach it by typing
 * `/admin/login`.
 *
 * BE CLEAR ABOUT WHAT THAT BUYS. The route string is compiled into the public
 * JavaScript bundle — anyone who greps it finds the path in seconds. Hiding the
 * link is NOT what protects this account, and treating it as though it were is
 * the dangerous reading.
 *
 * What protects it is on the server, and all of it already exists:
 *   • the auth rate limiter — 10 attempts per 15 minutes, keyed on client IP
 *     (which is why the nginx template must forward `X-Forwarded-For`, or every
 *     request looks like 127.0.0.1 and one bucket is shared by everyone);
 *   • lockout after 5 consecutive failures, applied inside a transaction so
 *     parallel guesses cannot each spend "one of five";
 *   • bcrypt at cost 12 on the stored hash.
 *
 * What the unlisted route DOES buy is still worth having: scanners and crawlers
 * never see it, and — more usefully day to day — the owner cannot wander onto a
 * screen demanding a password he does not have.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export interface AdminLoginFormProps extends Omit<React.ComponentProps<"div">, "onSubmit"> {
  staySignedIn: boolean
  onStayChange: (value: boolean) => void
  busy: boolean
  error: string | null
  onSubmit: (values: AdminLoginInput) => void
}

export function AdminLoginForm({
  className,
  staySignedIn,
  onStayChange,
  busy,
  error,
  onSubmit,
  ...props
}: AdminLoginFormProps) {
  const { t } = useTranslation()

  const form = useForm<AdminLoginInput>({
    resolver: zodResolver(AdminLoginSchema),
    mode: "onTouched",
    defaultValues: { email: "", password: "" },
  })

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          {/* `noValidate` — see the note in `login-form.tsx`. The browser's own
            * bubble cannot be styled or translated (§11.4). */}
          <form className="p-6 md:p-8" noValidate onSubmit={form.handleSubmit(onSubmit)}>
            <FieldGroup>
              <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">{t("auth.adminTitle")}</h1>
                <p className="text-balance text-muted-foreground">{t("auth.adminSubtitle")}</p>
              </div>

              {error === null ? null : (
                <Alert variant="destructive">
                  <AlertTitle>{t("auth.couldNotSignIn")}</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Field data-invalid={form.formState.errors.email !== undefined}>
                <FieldLabel htmlFor="email">{t("lbl.email")}</FieldLabel>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  aria-invalid={form.formState.errors.email !== undefined}
                  {...form.register("email")}
                />
                {form.formState.errors.email === undefined ? null : (
                  <FieldError>{t("err.emailInvalid")}</FieldError>
                )}
              </Field>

              <Field data-invalid={form.formState.errors.password !== undefined}>
                <FieldLabel htmlFor="password">{t("lbl.password")}</FieldLabel>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={form.formState.errors.password !== undefined}
                  {...form.register("password")}
                />
                {form.formState.errors.password === undefined ? null : (
                  <FieldError>{t("err.passwordInvalid")}</FieldError>
                )}
              </Field>

              <Field>
                <div className="flex items-center justify-between gap-4">
                  <FieldLabel htmlFor="admin-stay" className="font-normal">
                    {t("lbl.staySignedIn")}
                  </FieldLabel>
                  <Switch id="admin-stay" checked={staySignedIn} onCheckedChange={onStayChange} />
                </div>
                {/* `...adminHelp`, not the owner's copy. This screen has no PIN,
                  * and the shared string said "not asked for your PIN again". */}
                <FieldDescription>{t("auth.staySignedInAdminHelp")}</FieldDescription>
              </Field>

              <Field>
                <Button type="submit" disabled={busy}>
                  {busy ? t("auth.signingIn") : t("btn.signIn")}
                </Button>
              </Field>
            </FieldGroup>
          </form>

          <div className="relative hidden items-center justify-center bg-muted md:flex">
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <span
                aria-hidden="true"
                className="flex size-14 items-center justify-center rounded-xl bg-brand text-2xl font-bold text-brand-foreground"
              >
                {[...t("app.name")][0] ?? ""}
              </span>
              <span className="text-lg font-semibold tracking-tight">{t("app.name")}</span>
              <span className="max-w-[16rem] text-balance text-sm text-muted-foreground">
                {t("app.tagline")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
